// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import styles from './OwnerFigures.module.css'
import OwnerDial from './OwnerDial'
import { ReadBar, ReadLegend } from './ReadBar'
import {
  OWNER_PERCENT_MIN_NOTES,
  type OwnerAggregate,
  type OwnerReadAggregate,
} from 'lib/dealhunt/types'
import {
  gaveOutCallout,
  hoursCallout,
  metaLine,
  ownerSentence,
  READS_FOOTNOTE,
  readSplit,
} from 'lib/owner-notes/owner-figures'

/**
 * The owner-figure components over `lib/owner-notes/owner-figures.ts` (the copy builders): the
 * sentence beside the dial (`OwnerSentence`, the rail card), the reads (`Reads`) and the 5+ body
 * (`Aggregate`, the band's example). (The owner column beside the review — design 2a — was
 * removed 2026-09-22; this file was `OwnerVerdict.tsx` until then.)
 */

/** The owner sentence as one paragraph, the buy-again figure in the dial's violet. */
export const OwnerSentence: React.VFC<{
  aggregate: OwnerAggregate
  gateMet: boolean
  className: string
}> = ({ aggregate, gateMet, className }) => {
  const { lead, rest } = ownerSentence(aggregate, gateMet)
  return (
    <p className={className}>
      <strong className={styles.figure}>{lead}</strong>
      {rest}
    </p>
  )
}

interface ReadsProps {
  reads: OwnerReadAggregate[]
  gateMet: boolean
}

/** "Compared with what they expected": one three-segment bar per answered read. */
export const Reads: React.VFC<ReadsProps> = ({ reads, gateMet }) => {
  const answered = reads.filter(read => read.answers > 0)
  if (answered.length === 0) {
    return null
  }
  return (
    <div className={styles.reads}>
      <div className={styles.readsHead}>
        <p className={styles.eyebrow}>{'Compared with what they expected'}</p>
        <ReadLegend />
      </div>
      <ul className={styles.readList} aria-label="How it compared with expectations">
        {answered.map(read => (
          <li key={read.id} className={styles.read}>
            <div className={styles.readHead}>
              <span className={styles.readLabel}>{read.label}</span>
              <span className={styles.readSplit}>{readSplit(read, gateMet)}</span>
            </div>
            <ReadBar read={read} />
            <span className={styles.readAnswered}>
              {`${read.answers} owner${read.answers === 1 ? '' : 's'} answered`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

interface AggregateProps {
  aggregate: OwnerAggregate
  gateMet: boolean
}

/** The 5+ body: dial + sentence, the hairline callouts, the reads. */
export const Aggregate: React.VFC<AggregateProps> = ({ aggregate, gateMet }) => {
  const total = aggregate.published_count
  const hours = hoursCallout(aggregate)
  const gaveOut = gaveOutCallout(aggregate)
  return (
    <div className={styles.body}>
      <div className={styles.lead}>
        <OwnerDial yes={aggregate.buy_again_yes} total={total} gateMet={gateMet} />
        <div className={styles.leadText}>
          <OwnerSentence aggregate={aggregate} gateMet={gateMet} className={styles.sentence} />
          <p className={styles.meta}>{metaLine(aggregate)}</p>
          {hours && (
            <div className={styles.callout}>
              <p className={styles.calloutEyebrow}>{'Battery, reported'}</p>
              <p className={styles.calloutText}>
                <strong>{hours.lead}</strong>
                {hours.rest}
              </p>
              <p className={styles.calloutFine}>{hours.fine}</p>
            </div>
          )}
          {gaveOut && (
            <div className={styles.callout}>
              <p className={styles.calloutEyebrow}>{'What gave out'}</p>
              <p className={styles.calloutText}>{gaveOut.text}</p>
              <p className={styles.calloutFine}>{gaveOut.fine}</p>
            </div>
          )}
        </div>
      </div>
      <Reads reads={aggregate.reads} gateMet={gateMet} />
      <p className={styles.fine}>
        {READS_FOOTNOTE}
        {!gateMet && ` Percentages appear once ${OWNER_PERCENT_MIN_NOTES} owners have written in.`}
      </p>
    </div>
  )
}
